// ANETT HORVATH — Unified Content Blocks Store
// Single source of truth for all renderable content.

import type { SupportedLang } from '../config/house.config';
import type { VisibilityStatus, MembershipAccess } from '../core/render-policy';

export type ContentBlockType = 'text' | 'image' | 'video' | 'section' | 'product' | 'link';

export interface ContentBlock {
  id: string;
  key: string;
  type: ContentBlockType;
  value_en: string;
  value_hu: string;
  value_de: string;
  value_it: string;
  value_fr: string;
  media_id: string | null;
  route_target: string | null;
  visibility_status: VisibilityStatus;
  membership_access: MembershipAccess;
  manual_lock: boolean;
  sort_order: number;
  updated_at: string;
}

const CB_KEY = 'ah_content_blocks';
const uid = () => crypto.randomUUID();
const now = () => new Date().toISOString();

function load(): ContentBlock[] {
  try { const r = localStorage.getItem(CB_KEY); return r ? JSON.parse(r) : seed(); } catch { return seed(); }
}
function save(items: ContentBlock[]) { localStorage.setItem(CB_KEY, JSON.stringify(items)); }

function seed(): ContentBlock[] {
  const ts = now();
  const b = (key: string, type: ContentBlockType, en: string, hu: string, de: string, it: string, fr: string, opts: Partial<ContentBlock> = {}): ContentBlock => ({
    id: uid(), key, type, value_en: en, value_hu: hu, value_de: de, value_it: it, value_fr: fr,
    media_id: null, route_target: null, visibility_status: 'visible', membership_access: 'public', manual_lock: false, sort_order: 0, updated_at: ts, ...opts
  });
  const items: ContentBlock[] = [
    // Landing / Home
    b('hero_title', 'text', 'ANETT HORVÁTH', 'ANETT HORVÁTH', 'ANETT HORVÁTH', 'ANETT HORVÁTH', 'ANETT HORVÁTH'),
    b('hero_subtitle', 'text', 'Private House of Luxury Fragrance & Design', 'Privát Luxus Parfüm & Design Ház', 'Privates Haus für Luxusparfüm & Design', 'Casa Privata di Profumo & Design di Lusso', 'Maison Privée de Parfum & Design de Luxe'),
    b('hero_image', 'image', '/assets/brand-editorial-spray.png', '/assets/brand-editorial-spray.png', '/assets/brand-editorial-spray.png', '/assets/brand-editorial-spray.png', '/assets/brand-editorial-spray.png'),
    b('hero_cta', 'link', 'Enter the House', 'Lépjen be a Házba', 'Betreten Sie das Haus', 'Entra nella Casa', 'Entrez dans la Maison', { route_target: '/waitlist' }),
    // Product
    b('product_aethera_title', 'text', 'AETHERA — Extrait de Parfum', 'AETHERA — Extrait de Parfum', 'AETHERA — Extrait de Parfum', 'AETHERA — Extrait de Parfum', 'AETHERA — Extrait de Parfum', { membership_access: 'member' }),
    b('product_aethera_desc', 'text', 'You were familiar with the stars long before this world.', 'Ismerted a csillagokat jóval e világ előtt.', 'Sie kannten die Sterne lange vor dieser Welt.', 'Conoscevi le stelle molto prima di questo mondo.', 'Vous connaissiez les étoiles bien avant ce monde.', { membership_access: 'member' }),
    b('product_aethera_image', 'image', '/assets/brand-aethera-packaging.png', '/assets/brand-aethera-packaging.png', '/assets/brand-aethera-packaging.png', '/assets/brand-aethera-packaging.png', '/assets/brand-aethera-packaging.png', { membership_access: 'member' }),
    b('product_moonoflife_title', 'text', 'Moon of Life — Extrait de Parfum', 'Moon of Life — Extrait de Parfum', 'Moon of Life — Extrait de Parfum', 'Moon of Life — Extrait de Parfum', 'Moon of Life — Extrait de Parfum', { membership_access: 'member' }),
    b('product_moonoflife_desc', 'text', 'A fragrance born of moonlight and intention.', 'Holdfényből és szándékból született illat.', 'Ein Duft aus Mondlicht und Absicht.', 'Un profumo nato dalla luce della luna e dall\'intenzione.', 'Un parfum né du clair de lune et de l\'intention.', { membership_access: 'member' }),
    b('product_moonoflife_image', 'image', '/assets/brand-moonoflife-product.png', '/assets/brand-moonoflife-product.png', '/assets/brand-moonoflife-product.png', '/assets/brand-moonoflife-product.png', '/assets/brand-moonoflife-product.png', { membership_access: 'member' }),
    // Story
    b('story_title', 'text', 'The Story', 'A Történet', 'Die Geschichte', 'La Storia', 'L\'Histoire'),
    b('story_body', 'text', 'Born from a singular vision — to create objects of lasting beauty that honor both craft and feeling.', 'Egyedülálló vízióból született — tartós szépségű tárgyakat alkotni, amelyek tisztelik a mesterséget és az érzést.', 'Geboren aus einer einzigartigen Vision — Objekte von dauerhafter Schönheit zu schaffen.', 'Nata da una visione singolare — creare oggetti di bellezza duratura.', 'Née d\'une vision singulière — créer des objets d\'une beauté durable.'),
    b('story_image', 'image', '/assets/brand-aethera-moodboard.png', '/assets/brand-aethera-moodboard.png', '/assets/brand-aethera-moodboard.png', '/assets/brand-aethera-moodboard.png', '/assets/brand-aethera-moodboard.png'),
    // Waitlist
    b('waitlist_title', 'text', 'Request Entry', 'Belépés kérése', 'Eintritt anfordern', 'Richiedi l\'ingresso', 'Demander l\'entrée'),
    b('waitlist_body', 'text', 'The House accepts members by invitation and application. Submit your interest below.', 'A Ház meghívás és jelentkezés alapján fogad tagokat.', 'Das Haus nimmt Mitglieder per Einladung und Bewerbung auf.', 'La Casa accetta membri su invito e domanda.', 'La Maison accepte les membres sur invitation et candidature.'),
    // Allocation
    b('allocation_title', 'text', 'Allocation', 'Allokáció', 'Zuteilung', 'Allocazione', 'Allocation', { membership_access: 'member' }),
    b('allocation_body', 'text', 'Select with intention. Each piece is allocated, never mass-produced.', 'Válasszon szándékkal. Minden darab allokált, soha nem tömeggyártott.', 'Wählen Sie mit Absicht. Jedes Stück wird zugeteilt.', 'Scegli con intenzione. Ogni pezzo è allocato.', 'Choisissez avec intention. Chaque pièce est allouée.', { membership_access: 'member' }),
    // Checkout
    b('checkout_title', 'text', 'Secure Checkout', 'Biztonságos fizetés', 'Sichere Kasse', 'Checkout sicuro', 'Paiement sécurisé', { membership_access: 'member' }),
    // Fallback
    b('fallback_teaser', 'text', 'The House is preparing. Return soon.', 'A Ház készülődik. Térjen vissza hamarosan.', 'Das Haus bereitet sich vor. Kommen Sie bald wieder.', 'La Casa si prepara. Torna presto.', 'La Maison se prépare. Revenez bientôt.'),
    b('fallback_image', 'image', '/assets/brand-editorial-spray.png', '/assets/brand-editorial-spray.png', '/assets/brand-editorial-spray.png', '/assets/brand-editorial-spray.png', '/assets/brand-editorial-spray.png'),
  ];
  save(items);
  return items;
}

// Public API
export function getAllContentBlocks(): ContentBlock[] { return load(); }

export function getBlock(key: string): ContentBlock | undefined {
  return load().find(b => b.key === key);
}

export function getBlockValue(key: string, lang: SupportedLang): string {
  const block = getBlock(key);
  if (!block) return '';
  const val = block[`value_${lang}` as keyof ContentBlock] as string;
  return val || block.value_en || '';
}

export function getBlocksByPrefix(prefix: string): ContentBlock[] {
  return load().filter(b => b.key.startsWith(prefix)).sort((a, b) => a.sort_order - b.sort_order);
}

export function upsertBlock(id: string | null, data: Partial<ContentBlock>): ContentBlock {
  const items = load();
  const ts = now();
  if (id) {
    const idx = items.findIndex(b => b.id === id);
    if (idx >= 0) {
      // Optimistic lock check
      if (data.updated_at && items[idx].updated_at !== data.updated_at) {
        throw new Error('CONFLICT: Record changed. Reload required.');
      }
      items[idx] = { ...items[idx], ...data, updated_at: ts };
      save(items);
      return items[idx];
    }
  }
  const block: ContentBlock = {
    id: uid(), key: data.key || '', type: data.type || 'text',
    value_en: data.value_en || '', value_hu: data.value_hu || '', value_de: data.value_de || '', value_it: data.value_it || '', value_fr: data.value_fr || '',
    media_id: data.media_id || null, route_target: data.route_target || null,
    visibility_status: data.visibility_status || 'visible', membership_access: data.membership_access || 'public',
    manual_lock: data.manual_lock || false, sort_order: data.sort_order || 0, updated_at: ts
  };
  items.push(block);
  save(items);
  return block;
}

export function deleteBlock(id: string) {
  save(load().filter(b => b.id !== id));
}

// Global update timestamp
export function getLastUpdate(): string {
  const items = load();
  if (items.length === 0) return new Date().toISOString();
  return items.reduce((max, b) => b.updated_at > max ? b.updated_at : max, items[0].updated_at);
}
